/**
 * ThemeToggle — the topbar's quick-cycle button.
 *
 * Single click advances `light → dark → system → light`. The icon shown is
 * the icon for the CURRENT preference (not the resolved theme), so the user
 * can tell at a glance whether they're locked to a mode or following the OS.
 *
 *   ☀  Light       — explicitly light
 *   🌙 Dark        — explicitly dark
 *   ✦  System      — follow OS (icon is `Monitor`)
 *
 * Why a separate component (not a primitive): this is a composition of one
 * Flint `IconButton`-shaped surface + one `useTheme` hook, with copy and
 * affordance specific to this product. Lifting to `@flint/ui` would force
 * the primitive to know about a Flint-specific store. The dropdown
 * primitives in `@flint/ui` are the reusable surface; this is the consumer.
 */
import { Moon, Sun, Monitor } from "lucide-react";
import { cn } from "@flint/ui";
import { useTheme, type ThemePreference } from "./theme-store.ts";

const PREFERENCE_LABEL: Record<ThemePreference, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

const PREFERENCE_NEXT: Record<ThemePreference, ThemePreference> = {
  light: "dark",
  dark: "system",
  system: "light",
};

export interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { preference, cyclePreference } = useTheme();

  const Icon = preference === "light" ? Sun : preference === "dark" ? Moon : Monitor;
  const next = PREFERENCE_NEXT[preference];

  return (
    <button
      type="button"
      onClick={cyclePreference}
      // Title shows the action; aria-label spells it out for screen readers.
      // Two separate strings on purpose — title is hover-only and short, the
      // aria-label is the canonical description.
      title={`Theme: ${PREFERENCE_LABEL[preference]}. Click for ${PREFERENCE_LABEL[next]}.`}
      aria-label={`Switch theme. Current: ${PREFERENCE_LABEL[preference]}. Next: ${PREFERENCE_LABEL[next]}.`}
      // Mirrors the NotificationsButton / topbar icon-button shape — 44px
      // hit target, hover bg-raised, focus ring-sky.
      className={cn(
        "inline-grid place-items-center h-11 w-11 rounded-sm text-muted hover:text-ink hover:bg-raised transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
        className,
      )}
    >
      <Icon size={16} strokeWidth={2} aria-hidden />
    </button>
  );
}
